<template>
  <div class="info-section">
    <h5>Tail Data</h5>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">Return Max:</span>
        <span class="value">{{ tail.returnMax }}°F</span>
      </div>
      <div class="info-item">
        <span class="label">Return Min:</span>
        <span class="value">{{ tail.returnMin }}°F</span>
      </div>
    </div>

    <!-- Supply Temperatures Array -->
    <div v-if="tail.supplys && tail.supplys.length" class="sub-section">
      <h6>Supply Temperatures</h6>
      <div class="array-display">
        <span v-for="(temp, index) in tail.supplys" :key="`supply-${index}`" class="array-item">
          {{ temp }}°F
        </span>
      </div>
    </div>

    <!-- Return Temperatures Array -->
    <div v-if="tail.returns && tail.returns.length" class="sub-section">
      <h6>Return Temperatures</h6>
      <div class="array-display">
        <span v-for="(temp, index) in tail.returns" :key="`return-${index}`" class="array-item">
          {{ temp }}°F
        </span>
      </div>
    </div>

    <!-- Tail Data Footnote -->
    <div class="tail-data-footnote">
      <small class="footnote-text">
        * This data represents the last {{ getTailDataCount() }} payload(s) for this cycle
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CycleTailDataSection',
  props: {
    tail: {
      type: Object,
      required: true
    }
  },
  methods: {
    getTailDataCount() {
      if (!this.tail) {
        return 0;
      }

      // Count the number of payloads based on the supply temperatures array length
      // Both supplys and returns should have the same length, so we use supplys
      const supplyCount = this.tail.supplys ? this.tail.supplys.length : 0;
      const returnCount = this.tail.returns ? this.tail.returns.length : 0;

      // Return the maximum count between supplys and returns, or 0 if neither exists
      return Math.max(supplyCount, returnCount);
    }
  }
};
</script>

<style lang="scss" scoped>
.info-section {
  margin-bottom: 24px;
  background: rgb(24, 32, 64);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgb(58, 74, 107);

  h5 {
    margin: 0 0 16px 0;
    color: #ffffff;
    font-size: 16px;
    font-weight: 600;
    border-bottom: 2px solid #4a9eff;
    padding-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;

    &::before {
      content: '📈';
      font-size: 14px;
    }
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;

    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      background: rgb(32, 40, 80);
      border-radius: 8px;
      border: 1px solid rgb(58, 74, 107);
      min-height: 52px;
      word-break: break-word;
      transition: all 0.2s ease;

      &:hover {
        background: rgb(42, 58, 107);
        border-color: #4a9eff;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      }

      .label {
        font-weight: 600;
        color: #ffffff;
        font-size: 13px;
        flex-shrink: 0;
        margin-right: 16px;
        min-width: 140px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .value {
        font-weight: 700;
        color: #ffffff;
        font-size: 14px;
        text-align: right;
        word-break: break-word;
        overflow-wrap: break-word;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
    }
  }

  .sub-section {
    margin-top: 20px;

    &:not(:first-of-type) {
      margin-top: 16px;
    }

    h6 {
      margin: 0 0 12px 0;
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 6px;

      &::before {
        content: '📋';
        font-size: 12px;
      }
    }

    .array-display {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;

      .array-item {
        background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
        color: #1976d2;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        border: 2px solid #1976d2;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(25, 118, 210, 0.3);
        }
      }
    }
  }

  .tail-data-footnote {
    margin-top: 12px;
    padding-top: 8px;
    border-top: 1px solid rgb(58, 74, 107);
  }

  .footnote-text {
    color: #b8c5d1;
    font-style: italic;
    font-size: 12px;
  }
}
</style>
